import {Carousel, Typography} from '@bonprix/design-system';
import React, {useEffect, useRef, useState} from 'react';
import RecommendationAPI from '@personalization/scripts/apis/recommendation-api';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import SliderLandmarkHelper from '@core/scripts/components/tracking/landmarks/slider-landmark-helper.js';
import ProductCard, {Touchpoint} from '@wishlist/components/product-card/ProductCard.jsx';
import Logger from "@core/scripts/components/logger.js";

export default function RecommendationSliderDeSy({
    dataUrl,
    paramString,
    productCardTexts,
    className = 'desy-recommendations',
    max_width = null
}) {

    const LAZY_LOADING_PIXEL_DISTANCE_FOR_RECO_LOAD = 800;
    const SLIDER_TYPE = "reco";
    const NUMBER_OF_ITEMS_BY_BREAKPOINT = [
        { minWidth: 1600, items: 6 },
        { minWidth: 1280, items: 6 },
        { minWidth: 992, items: 5 },
        { minWidth: 768, items: 4 },
        { minWidth: 480, items: 3 },
        { minWidth: 0, items: 2 }
    ];
    const SLIDE_CUT = 1/3;

    const [recoData, setRecoData] = useState(null);
    const [numberOfItemsPerPage, setNumberOfItemsPerPage] = useState(getNumberOfItemsPerPage());
    const [carouselPadding, setCarouselPadding] = useState(calculateCarouselPadding());
    const inViewRef = useRef(null);
    let observer;

    function getNumberOfItemsPerPage() {
        return NUMBER_OF_ITEMS_BY_BREAKPOINT.find(
            bp => window.innerWidth >= bp.minWidth
        )?.items;
    }

    function calculateCarouselPadding() {
        const sliderWidth = max_width ? Math.min(window.innerWidth, max_width) : window.innerWidth;
        const visibleItems = getNumberOfItemsPerPage() + 0.5;
        const itemWidth = sliderWidth / visibleItems;
        return itemWidth * SLIDE_CUT;
    }

    // Loads data when slider is within LAZY_LOADING_PIXEL_DISTANCE_FOR_RECO_LOAD pixels of the viewport
    useEffect(() => {
        if (inViewRef.current) {
            observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        loadContentAndFillModel();
                        observer.unobserve(inViewRef.current);
                    }
                },
                {
                    rootMargin: `0px 0px ${LAZY_LOADING_PIXEL_DISTANCE_FOR_RECO_LOAD}px 0px`,
                }
            );
            // if (inViewRef.current) {
                observer.observe(inViewRef.current);
            // }
        }
    }, [inViewRef]);

    // Loads and caches recommendations
    function loadContentAndFillModel() {
        if (!recoData) {
            RecommendationAPI.get(dataUrl, paramString, 'Fetching Product Recommendations failed').then((recoData) => {
                setRecoData(recoData);
                const event = {items: recoData?.recommendations, displayedItemCount: numberOfItemsPerPage};
                SliderLandmarkHelper.createAndSendSliderVisibilityLandmark(SLIDER_TYPE, event, recoData.landmarkData, true);
            }).catch(onFetchingRecommendationsFailed);
        }
    }

    function onFetchingRecommendationsFailed(error) {
        Logger.debug(error);
    }

    useEffect(() => {
        const handleResize = () => {
            const newNumberOfItemsPerPage = getNumberOfItemsPerPage();
            setNumberOfItemsPerPage(newNumberOfItemsPerPage);
            setCarouselPadding(calculateCarouselPadding());
        };

        MessageBroker.subscribe(Messages.WINDOW.RESIZED, handleResize)

        return () => {
            MessageBroker.unsubscribe(Messages.WINDOW.RESIZED, handleResize);
        };
    }, []);

    const onProductCardClicked = (product) => {
        const {assetId, articleNumber, digistyleId, soldOut} = product;
        const availabilityStatus = soldOut ? 'SOLDOUT' : 'AVAILABLE';
        SliderLandmarkHelper.createAndSendSlideClickedLandmark(
            SLIDER_TYPE,
            assetId,
            articleNumber,
            digistyleId,
            availabilityStatus,
            landmarkData,
            true
        );
    };

    const onSlideChanged = () => {
        SliderLandmarkHelper.createSendSlideChangedLandmark(landmarkData, true);
    };

    if (!recoData) {
        return <div ref={inViewRef} />;
    }

    const {landmarkData, title, recommendations} =
        recoData;

    if (recommendations?.length === 0) {
        return <div ref={inViewRef} />;
    }

    return(
        <>
            <div className={className} ref={inViewRef}>
                <div className={`${className}__headline`}>
                    <Typography variant="bonprix-h4" component="span">
                        {title}
                    </Typography>
                </div>
                <Carousel label="Recommendations"
                          gap={4}
                          arrows={true}
                          pagination={false}
                          padding={{
                              right: `${carouselPadding}px`,
                          }}
                          perPage={numberOfItemsPerPage}
                          perMove={numberOfItemsPerPage}
                          onMove={onSlideChanged}
                >
                    {recommendations.map((product, index) => (
                        <Carousel.Slide key={'recommendation-carousel-' + index}>
                            <ProductCard
                                item={product}
                                touchpoint={Touchpoint.RECOMMENDATION}
                                texts={productCardTexts}
                                onItemClick={() => onProductCardClicked(product)}
                            />
                        </Carousel.Slide>
                ))}
                </Carousel>
            </div>
            <div className="product-recommendations__divider"/>
        </>
    )

}
